import { lowerBound } from '@/shared/charts/renderers/echarts/time-series-chart/utils/lowerBound';

export type LinePoint = [number, number];

/** ECharts leaves a line open at an entry without a value, instead of joining its neighbours. */
type LineBreak = [number, null];

export type LineDatum = LinePoint | LineBreak;

function hasGapBetween(gapIndexes: number[], fromIndex: number, toIndex: number): boolean {
  const nextGap = gapIndexes[lowerBound(gapIndexes, fromIndex + 1)];

  return nextGap !== undefined && nextGap <= toIndex;
}

function isLinePoint(datum: LineDatum | undefined): datum is LinePoint {
  return datum !== undefined && datum[1] !== null;
}

/**
 * The tuples at `indexes`, with a break wherever the raw data has a gap between two of them.
 * The raw data decides, not the drawn points: zoomed out, two drawn points of a dense stretch can
 * be far apart with no gap between them.
 */
export function toLineData(tuples: LinePoint[], gapIndexes: number[], indexes: number[]): LineDatum[] {
  return indexes.flatMap((index, position): LineDatum[] => {
    const previousIndex = indexes[position - 1];
    const isAfterGap = previousIndex !== undefined && hasGapBetween(gapIndexes, previousIndex, index);

    return isAfterGap ? [[tuples[previousIndex][0], null], tuples[index]] : [tuples[index]];
  });
}

/** Points with no neighbour to join on either side: the line alone draws nothing for them. */
export function findLonePoints(data: LineDatum[]): LinePoint[] {
  return data.filter(
    (datum, position): datum is LinePoint =>
      isLinePoint(datum) && !isLinePoint(data[position - 1]) && !isLinePoint(data[position + 1])
  );
}
